import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExpandOutlined,
  ExportOutlined,
  FileAddOutlined,
  HistoryOutlined,
  MailOutlined,
  RedoOutlined,
  SyncOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import styled, { css } from 'styled-components';

import { ow } from 'containers/Overwatch/theme/tokens';

import { orbitType } from '../../styles';
import { SupportAction, SupportActionIcon, SupportTicketStatus } from '../types';

// Values from the Figma support frames.
export const sp = {
  bubble: ow.card,
  inset: ow.inset,
  border: ow.border,
  text: ow.textPrimary,
  muted: ow.textSecondary,
  primary: ow.accent,
  link: ow.info,
  warning: ow.warning,
  danger: ow.danger,
  success: ow.success
} as const;

type OrbitTypeRole = keyof typeof orbitType;

const font = (role: OrbitTypeRole, weight: 400 | 700 = 400) => css`
  ${orbitType[role]}
  font-weight: ${weight};
`;

/**
 * Gap above every action row and link: base Orbit's answer rhythm (`OrbitAnswer` Root and
 * `OrbitAnalysisCard`, both `> * + *` 8px). Doubled class so a parent Stack's own gap cannot
 * pull the row back against the content above it.
 */
export const ACTION_GAP = 8;

// UI-only build: every action renders but does nothing until it is wired.
export const noop = (): void => undefined;

export const SUPPORT_ICONS: Record<SupportActionIcon, React.ComponentType> = {
  calendar: CalendarOutlined,
  download: DownloadOutlined,
  open: ExportOutlined,
  arrow: ArrowRightOutlined,
  ticket: FileAddOutlined,
  history: HistoryOutlined,
  retry: RedoOutlined,
  sync: SyncOutlined,
  check: CheckOutlined,
  expand: ExpandOutlined,
  chart: BarChartOutlined,
  mail: MailOutlined,
  clock: ClockCircleOutlined,
  warning: WarningOutlined
};

export const SupportIcon: React.FC<{ icon?: SupportActionIcon | null }> = ({ icon }) => {
  if (icon === undefined || icon === null) {
    return null;
  }
  const Icon = SUPPORT_ICONS[icon];
  return (
    <span aria-hidden="true">
      <Icon />
    </span>
  );
};

export const Intro = styled.p<{ $panel?: boolean }>`
  margin: 0;
  color: ${sp.muted};
  ${font('conversation')}
`;

export const Stack = styled.div<{ $gap: number }>`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${({ $gap }) => $gap}px;
  }
`;

export const AnswerCard = styled(Stack)`
  padding: 13px;
  border: 1px solid ${sp.border};
  border-radius: 12px;
  background: ${sp.inset};
`;

export const ResultRow = styled(Stack)`
  padding: 11px 12px;
  border: 1px solid ${sp.border};
  border-radius: 10px;
  background: ${sp.bubble};
`;

export const RowTitle = styled.h5`
  margin: 0;
  color: ${sp.text};
  ${font('containerTitle', 700)}
`;

export const RowBody = styled.p`
  margin: 0;
  color: ${sp.muted};
  ${font('containerBody')}
`;

export const Pill = styled.span<{ $color: string; $outlined?: boolean }>`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: ${({ $outlined }) => ($outlined ? '4px 10px' : '1px 6px')};
  border: ${({ $outlined, $color }) => ($outlined ? `1px solid ${$color}` : 'none')};
  border-radius: 999px;
  background: ${({ $color }) => `${$color}24`};
  color: ${({ $color }) => $color};
  white-space: nowrap;
  ${font('helping', 700)}

  ${({ $outlined }) =>
    $outlined
      ? css`
          && + * {
            margin-top: ${ACTION_GAP}px;
          }
        `
      : ''}
`;

export const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-left: -8px;

  && {
    margin-top: ${ACTION_GAP - 4}px;
  }

  > * {
    margin: 4px 0 0 8px;
  }
`;

const Button = styled.button<{ $kind: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  min-height: 35px;
  padding: 0 12px;
  border: 1px solid ${({ $kind }) => ($kind === 'primary' ? sp.primary : sp.border)};
  border-radius: 9px;
  background: ${({ $kind }) => ($kind === 'primary' ? sp.primary : sp.bubble)};
  color: ${({ $kind }) => ($kind === 'primary' ? '#FFFFFF' : sp.text)};
  cursor: pointer;
  ${font('containerBody', 700)}

  > * + * {
    margin-left: 7px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 2px 0;
  border: none;
  background: transparent;
  color: ${sp.link};
  cursor: pointer;

  && {
    margin-top: ${ACTION_GAP}px;
  }
  ${font('containerBody', 700)}

  > * + * {
    margin-left: 6px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

interface ISupportActionButtonProps {
  action: SupportAction;
  disabled?: boolean;
  describedBy?: string;
}

/** Downloads are not in the FR, so they render disabled until a contract covers them. */
const isDownload = (action: SupportAction): boolean =>
  action.icon === 'download' || action.id.startsWith('download') || action.id === 'view_pdf';

/** FR §4.20: Book a demo is a deep link to Explore More, never a submit. */
export const ORBIT_EXPLORE_PATH = '/p/orbit?view=explore';

const DisabledTip = styled.span`
  display: inline-flex;
`;

export const SupportActionButton: React.FC<ISupportActionButtonProps> = ({
  action,
  disabled,
  describedBy
}) => {
  const navigate = useNavigate();
  const download = isDownload(action);
  const onClick = action.id === 'book_demo' ? () => navigate(ORBIT_EXPLORE_PATH) : noop;
  const button =
    action.kind === 'link' ? (
      <LinkButton
        type="button"
        disabled={download || disabled}
        onClick={onClick}
        data-action-id={action.id}
      >
        <span>{action.label}</span>
        <SupportIcon icon={action.icon} />
      </LinkButton>
    ) : (
      <Button
        type="button"
        $kind={action.kind}
        disabled={download || disabled}
        aria-describedby={describedBy}
        onClick={onClick}
        data-action-id={action.id}
      >
        <SupportIcon icon={action.icon} />
        <span>{action.label}</span>
      </Button>
    );
  if (!download) {
    return button;
  }
  // A disabled button fires no hover, so the tip hangs off a wrapper (cross-browser.md § 2).
  return (
    <Tooltip title={DOWNLOAD_UNAVAILABLE} trigger={['hover', 'focus']}>
      <DisabledTip data-testid="orbit-support-download-unavailable">{button}</DisabledTip>
    </Tooltip>
  );
};

const DOWNLOAD_UNAVAILABLE = 'Not available yet';

export const STATUS_COLOR: Record<SupportTicketStatus, string> = {
  open: sp.link,
  pending: sp.warning,
  investigating: sp.link,
  resolved: sp.success,
  closed: sp.muted
};

export const StatusText = styled.span<{ $status: SupportTicketStatus }>`
  color: ${({ $status }) => STATUS_COLOR[$status]};
  font-weight: 700;
`;

export const Panel = styled.section`
  border: 1px solid ${sp.border};
  border-radius: 12px;
  background: ${sp.bubble};
  overflow: hidden;
`;

export const PanelHead = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 13px;
  border-bottom: 1px solid ${sp.border};
`;

export const PanelTitle = styled.h4`
  margin: 0;
  color: ${sp.text};
  ${font('containerTitle', 700)}
`;

export const PanelSubtitle = styled.p`
  margin: 2px 0 0;
  color: ${sp.muted};
  ${font('helping')}
`;

export const PanelBody = styled(Stack)`
  padding: 6px 10px 13px;
`;

export const KeyValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 35px;
  padding: 0 24px;
  border-bottom: 1px solid ${sp.border};
  ${font('containerBody')}

  > span:first-child {
    color: ${sp.muted};
  }

  > span:last-child {
    margin-left: 10px;
    color: ${sp.text};
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }
`;

export const CompactTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;

  th,
  td {
    padding-right: 8px;
    white-space: nowrap;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  th {
    padding-top: 8px;
    padding-bottom: 8px;
    color: ${sp.muted};
    text-align: left;
    white-space: nowrap;
    ${font('helping', 700)}
  }

  td {
    padding-top: 9px;
    padding-bottom: 9px;
    border-top: 1px solid ${sp.border};
    color: ${sp.muted};
    vertical-align: middle;
    ${font('helping')}
  }

  td:nth-child(2) {
    width: 100%;
    min-width: 0;
    max-width: 0;
  }

  td:first-child {
    color: ${sp.text};
    font-weight: 700;
    white-space: nowrap;
  }
`;

export const VisuallyHidden = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Truncated = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TitleCell: React.FC<{ title: string }> = ({ title }) => (
  <td>
    <Tooltip
      autoAdjustOverflow
      getPopupContainer={trigger => trigger.parentElement ?? document.body}
      placement="top"
      title={title}
      trigger={['hover', 'focus']}
    >
      <Truncated tabIndex={0} data-testid="orbit-support-title-cell">
        {title}
      </Truncated>
    </Tooltip>
  </td>
);
